/**
 * The Next card (core next.ts): the one thing this person should do right now, on Today for staff.
 * One short "why now" line, exactly one primary button that does the action (opens the log form, the fix, the check-in),
 * and the count lives only on the "Everything today (N more)" fold, never twice. When the last Next is done, a check draws (about 220 ms) and the
 * new card slides up; both stop with prefers-reduced-motion (styles.css .next-*).
 */
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { allergyOrderOffered, justFinished, type NextAction, type NextGlyph, type NextResult } from "@logwave/core";
import { savedOf } from "../lib/checkoff.ts";
import { initialsOf } from "../lib/labels.ts";
import { useApi, useSession } from "../session.tsx";
import { taskKind, useTaskDone } from "./ItemCard.tsx";
import { AssignLine, useStepIn } from "./Delegate.tsx";
import { PushAskCard } from "./PushReminders.tsx";
import { UsageAskCard } from "./UsageAsk.tsx";
import { Icon, type IconName } from "./Icons.tsx";
import { Button, LinkButton, Pill, useToast } from "./ui.tsx";

/** One glyph set, drawn from the same Icons.tsx as every other card. */
export const GLYPH: Record<NextGlyph, IconName> = { thermo: "thermo", flask: "flask", restroom: "restroom", do: "do", hands: "hands", learn: "learn", alert: "alert", check: "check", report: "report", write: "write", clock: "clock" };

/** Where a non-task action goes on the web. */
export function nextPath(a: NextAction): string {
  switch (a.kind) {
    case "checkin": return "/check-in";
    case "learn": return "/learn";
    // A cooling batch's reading opens that batch (core cooling.ts), not a blank log form.
    case "check": return a.coolingId ? `/food/cooling?id=${encodeURIComponent(a.coolingId)}` : `/do?check=${encodeURIComponent(a.key)}`;
    case "fix": return `/do/fix/${encodeURIComponent(a.entryId)}${a.askId ? `?ask=${encodeURIComponent(a.askId)}` : ""}`;
    case "incident": return `/report#${encodeURIComponent(a.id)}`;
    case "report": return "/report";
    // Food past its date or time (core feed.ts F1): the discard log for that food, one tap (Food.tsx ?discard=).
    case "throw_out": return `/food?discard=${encodeURIComponent(a.foodId)}`;
  }
}

/**
 * "Do it" for a task: a checklist task is one tap in place (the same call the check card makes, with Undo); everything
 * else opens its form. The Next card and the card deck share it, so they can never do different things.
 */
export function useDoNext(title: string, action: NextAction, reload: () => Promise<void> | void) {
  const task = useTaskDone();
  // A one-tap task ticks here; a task that needs a reading, an answer or a photo goes to its card (Do opens it), never ticks.
  const tap = action.kind === "check" && action.templateId === "task_done" && !!action.taskId && taskKind(title) === "tap";
  async function markDone() {
    if (action.kind !== "check" || !action.taskId) return;
    if (await task.complete({ label: title, taskId: action.taskId }, {}, { onUndone: () => void reload() })) await reload();
  }
  return { tap, busy: task.busy, markDone };
}

/** What the last visit showed, so a finished Next can be noticed after the log form closes and Today comes back. */
let last: { id: string; title: string; at: number } | undefined;

export function NextCard({ res, reload, children }: { res: NextResult; reload: () => Promise<void> | void; /** Quiet extras under the button (the how-to fold). Never above it. */ children?: ReactNode }) {
  const [done, setDone] = useState<string | null>(() => justFinished(last, res, Date.now()));
  // Stays after the "Done" line fades, until the person answers it (or leaves).
  const [asked, setAsked] = useState(false);
  useEffect(() => { if (done) setAsked(true); }, [done]);
  const n = res.next;
  // Runs on mount (compares with the last visit) and whenever the Next changes (compares with the one before it).
  useEffect(() => {
    setDone(justFinished(last, res, Date.now()));
    last = { id: n.id, title: n.title, at: Date.now() };
  }, [n.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(null), 4000);
    return () => clearTimeout(t);
  }, [done]);

  const s = useSession();
  const a = n.primary.action;
  const { tap, busy, markDone } = useDoNext(n.title, a, reload);
  // Given to someone else: the button is "I'll do it", and the step-in is recorded as the work starts (core next.ts N6).
  const stepIn = useStepIn();
  const navigate = useNavigate();
  const stepThen = (go: () => void) => () => { if (n.stepIn) void stepIn(n.stepIn.key); go(); };
  return (
    <section className="next" aria-label="Next">
      {done && <p className="next-done" role="status"><span className="next-check" aria-hidden="true"><Icon name="check" size={16} /></span>Done: {done}</p>}
      <article key={n.id} className={`fcard next-card fcard-${n.tone}${done ? " next-in" : ""}`} data-next-kind={n.kind}>
        <header className="fcard-head">
          <span className="fcard-av" aria-hidden="true"><Icon name={GLYPH[n.glyph]} size={20} /></span>
          <span className="fcard-kind">Next</span>
          {n.when && <span className="fcard-when"><Pill tone={n.tone === "warn" ? "warn" : "line"} icon="clock">{n.when}</Pill></span>}
        </header>
        <div className="fcard-body">
          <h2 className="fcard-title">{n.title}</h2>
          {n.assign && <AssignLine assign={n.assign} />}
          <p className="next-why">{n.why}</p>
          {tap
            ? <Button big block icon="check" busy={busy} onClick={stepThen(() => void markDone())}>{n.primary.label}</Button>
            : n.stepIn
              ? <Button big block icon="chevron" iconEnd onClick={stepThen(() => navigate(nextPath(a)))}>{n.primary.label}</Button>
              : <LinkButton big block icon={a.kind === "checkin" ? "hands" : "chevron"} iconEnd={a.kind !== "checkin"} to={nextPath(a)}>{n.primary.label}</LinkButton>}
          {/* The 12:15 question can come any minute: the allergy order is one quiet tap under the one primary button (core allergens.ts). */}
          {allergyOrderOffered(n, s.location.menuItemIds) && <LinkButton small variant="quiet" icon="allergen" to="/allergy-order">Start an allergy order</LinkButton>}
          {children}
        </div>
      </article>
      {/* The value moment (B = MAT): a check just got done, so "a nudge when the next one is due" means something.
          Below the Next card: nothing tappable ever sits above its one primary button (N4). */}
      {asked && <PushAskCard moment="check_logged" />}
      {/* The fun ask for linked usage counts (core USAGE_ASK): same moment, once, only in a build that sends linked analytics. */}
      {asked && <UsageAskCard />}
    </section>
  );
}
